import { z } from "zod";
import {
  currencySchema,
  emailSchema,
  instantSchema,
  longTextSchema,
  optionalText,
  phoneSchema,
  plainDateSchema,
  plainTimeSchema,
  reasonSchema,
  shortTextSchema,
  slugSchema,
  timezoneSchema,
  ulidSchema,
} from "./primitives.js";

export const EVENT_STATUSES = [
  "draft",
  "published",
  "in_progress",
  "complete",
  "archived",
  "cancelled",
] as const;
export const eventStatusSchema = z.enum(EVENT_STATUSES);

export const EVENT_VISIBILITIES = ["public", "private"] as const;

/**
 * What one act of checking in covers (D66).
 *
 * `session` is the gate — a dancer arrives once on the Saturday and is present for everything they
 * entered that day. `competition` is the staging area — each competition takes its own check-in,
 * which is what a community running separate call-ups needs in order to know who is standing there.
 * Both write the same per-entry rows, so nothing downstream of the gate changes with the setting.
 */
export const CHECK_IN_UNITS = ["session", "competition"] as const;
export const checkInUnitSchema = z.enum(CHECK_IN_UNITS);

/**
 * The prose blocks of the public event page. One object rather than eight columns because they
 * are always read together, never queried by, and the set will grow as communities ask for more.
 * Every block optional: a poster with no parking note simply has no parking section.
 */
export const eventInfoSchema = z.object({
  registrationInstructions: longTextSchema.optional(),
  generalRules: longTextSchema.optional(),
  cancellation: longTextSchema.optional(),
  accessibility: longTextSchema.optional(),
  parking: longTextSchema.optional(),
  travel: longTextSchema.optional(),
  accommodation: longTextSchema.optional(),
  emergency: longTextSchema.optional(),
});

export type EventInfo = z.infer<typeof eventInfoSchema>;

/**
 * Which credential types judges may use for an event (D15).
 *
 * Both are allowed by default. A community that vets judges weeks ahead can turn access codes
 * off; one that picks judges at the drum can turn invitations off; most will leave both on and
 * use whichever suits the category in front of them.
 */
export const judgeAccessPolicySchema = z.object({
  allowAccessCodes: z.boolean().default(true),
  allowInvitations: z.boolean().default(true),
  /** Digits in a generated access code. Six is short enough to read aloud across a drum. */
  accessCodeLength: z.number().int().min(4).max(10).default(6),
  /** Codes expire at the end of their session by default; this extends that in minutes. */
  accessCodeGraceMinutes: z.number().int().min(0).max(720).default(60),
  /**
   * The longest a freshly-minted code can live, whatever the schedule says.
   *
   * A session's scheduled end is a plan, and on a bad day it is wrong by hours or missing
   * altogether. This is the ceiling that does not depend on the plan being right: a code is good
   * until its session ends **or** this long from the moment it was read out, whichever comes
   * first. Six hours covers a full afternoon of judging without leaving a live credential in
   * somebody's pocket overnight.
   */
  accessCodeMaxHours: z.number().int().min(1).max(24).default(6),
});

export type JudgeAccessPolicy = z.infer<typeof judgeAccessPolicySchema>;

/**
 * A judging panel size (D56) — the target the coverage badge counts toward, not a cap. One is the
 * floor (a single judge is a real, if small, panel); the ceiling is generous because it is guidance,
 * never a limit on assignment.
 */
export const panelSizeSchema = z.number().int().min(1).max(99);

export const createEventSchema = z
  .object({
    slug: slugSchema,
    name: shortTextSchema,
    description: optionalText(longTextSchema),
    startsOn: plainDateSchema,
    endsOn: plainDateSchema,
    timezone: timezoneSchema,
    venue: optionalText(shortTextSchema),
    /** The house target panel size, five by default (D56). Overridden per competition. */
    defaultPanelSize: panelSizeSchema.default(5),
    judgeAccessPolicy: judgeAccessPolicySchema.optional(),
  })
  .refine((value) => value.endsOn >= value.startsOn, {
    message: "The end date cannot be before the start date",
    path: ["endsOn"],
  });

/** Nullable where a value can be removed — `venue: null` clears it, an absent field is left alone. */
export const updateEventSchema = z.object({
  name: shortTextSchema.optional(),
  publicTitle: shortTextSchema.nullable().optional(),
  description: longTextSchema.nullable().optional(),
  summary: longTextSchema.nullable().optional(),
  startsOn: plainDateSchema.optional(),
  endsOn: plainDateSchema.optional(),
  timezone: timezoneSchema.optional(),
  venue: shortTextSchema.nullable().optional(),
  venueAddress: longTextSchema.nullable().optional(),
  city: shortTextSchema.nullable().optional(),
  region: shortTextSchema.nullable().optional(),
  postalCode: shortTextSchema.nullable().optional(),
  country: shortTextSchema.nullable().optional(),
  mapUrl: z.url().max(1000).nullable().optional(),
  contactName: shortTextSchema.nullable().optional(),
  contactEmail: emailSchema.nullable().optional(),
  contactPhone: phoneSchema.nullable().optional(),
  visibility: z.enum(EVENT_VISIBILITIES).optional(),
  currency: currencySchema.optional(),
  defaultPanelSize: panelSizeSchema.optional(),
  /** What one check-in covers, and whether a participant may record their own (D66). */
  checkInUnit: checkInUnitSchema.optional(),
  selfCheckIn: z.boolean().optional(),
  /** Whether arriving sends the participant a confirmation email. Off by default (D66's posture). */
  checkInEmail: z.boolean().optional(),
  info: eventInfoSchema.nullable().optional(),
  judgeAccessPolicy: judgeAccessPolicySchema.partial().optional(),
});

/**
 * Duplicating an event copies configuration by flag; registrations, scores and payouts never
 * copy, and there is deliberately no flag that could ask for them (see `clone.ts`'s allow-list).
 */
export const duplicateEventSchema = z.object({
  options: z
    .object({
      /** Sessions, registration windows and the event team travel with the details. */
      details: z.boolean().default(true),
      competitions: z.boolean().default(true),
      categories: z.boolean().default(true),
      phases: z.boolean().default(true),
      scoringConfigs: z.boolean().default(true),
      /** Accepted for forward compatibility; forms are community templates, not event children. */
      forms: z.boolean().default(false),
      prizeRules: z.boolean().default(true),
      announcements: z.boolean().default(false),
    })
    .prefault({}),
});

export type DuplicateEventOptions = z.infer<typeof duplicateEventSchema>["options"];

/** Cancelling demands a reason — it is public, and the audit trail is the record of why. */
export const cancelEventSchema = z.object({
  reason: reasonSchema,
});

/**
 * A session — the grain money, entry and judging all share (D14).
 *
 * Times are wall-clock strings, not instants. The organiser types "1:00pm" because that is what
 * the poster says; the instant is derived on write and re-derived if the timezone is later
 * corrected.
 */
export const createSessionSchema = z.object({
  name: shortTextSchema,
  date: plainDateSchema,
  startsLocal: plainTimeSchema.optional(),
  grandEntryLocal: plainTimeSchema.optional(),
  endsLocal: plainTimeSchema.optional(),
  sortOrder: z.number().int().min(0).default(0),
});

/**
 * A session edit. The wall-clock times are nullable rather than merely optional: `null` clears a
 * time ("Grand Entry is announced on the day" is a real answer), while an absent field leaves it
 * untouched — a distinction `.partial()` cannot express.
 */
export const updateSessionSchema = z.object({
  name: shortTextSchema.optional(),
  date: plainDateSchema.optional(),
  startsLocal: plainTimeSchema.nullable().optional(),
  grandEntryLocal: plainTimeSchema.nullable().optional(),
  endsLocal: plainTimeSchema.nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const WINDOW_STATUSES = ["scheduled", "open", "closed"] as const;

/**
 * A registration window.
 *
 * Everything is optional, which is the whole generalisation: a single deadline for the event is
 * one window with no sessions; "registration held daily" is one window per session (or one linked
 * to the days it runs); rolling registration is a window with no bounds; and a window announced on
 * the day is created with no times at all and opened by hand (D14, D20, D49).
 */
export const WINDOW_PURPOSES = ["registration", "check_in"] as const;
export const windowPurposeSchema = z.enum(WINDOW_PURPOSES);

export const createRegistrationWindowSchema = z.object({
  /**
   * What this window governs. Check-in asks the identical question registration does — is this open,
   * for this session, for this competition, right now — so it is the same shape resolved by the same
   * rule, named rather than duplicated.
   */
  purpose: windowPurposeSchema.default("registration"),
  /** Empty or absent means the window governs every session — event-wide (D49). */
  sessionIds: z.array(ulidSchema).optional(),
  opensLocal: plainTimeSchema.optional(),
  opensOn: plainDateSchema.optional(),
  closesLocal: plainTimeSchema.optional(),
  closesOn: plainDateSchema.optional(),
  competitionIds: z.array(ulidSchema).optional(),
  notes: optionalText(longTextSchema),
});

/**
 * A window edit restates the window. Every field of a window is optional even on create — "no
 * bounds" and "event-wide" are meaningful values, not omissions — so a partial patch cannot tell
 * "leave the closing time alone" from "remove the closing time". The update therefore replaces
 * the whole configuration, exactly as the form that submits it displays it.
 */
export const updateRegistrationWindowSchema = createRegistrationWindowSchema;

/** Opening or closing a window by hand during the event. Routine, not exceptional. */
/**
 * Forcing a window open or closed by hand — and letting it go again.
 *
 * `scheduled` is the third state and it is not decoration: it is the way *back*. The column has
 * always held all three, but this accepted only two, so an override was one-way. A volunteer who
 * opened check-in at eight for the people already at the gate had no way to hand it back to the
 * clock, and the window then stayed open past the end of the event — which on a check-in window
 * means people can still be marked present, and being marked present is what puts somebody on a
 * payout sheet. "Undo" is the operation that was missing, so it is spelled the same way as the
 * other two rather than being a separate route.
 */
export const setWindowStateSchema = z.object({
  status: z.enum(["scheduled", "open", "closed"]),
  notes: optionalText(longTextSchema),
});

/**
 * The same act, addressed by session rather than by window.
 *
 * The gate is opened by the person standing at it, and that person knows which session they are
 * working — not which of the event's windows governs it, or whether one exists at all. So this names
 * the session and lets the repository find or write the window (see `holdCheckInGate`).
 */
export const holdCheckInGateSchema = z.object({
  sessionId: ulidSchema,
  status: z.enum(["scheduled", "open", "closed"]),
});

export const createEventTeamMemberSchema = z.object({
  userId: ulidSchema.optional(),
  displayName: shortTextSchema,
  /** A drum is a group, not a person. */
  groupName: optionalText(shortTextSchema),
  roleLabel: shortTextSchema,
  sortOrder: z.number().int().min(0).default(0),
});

export const ANNOUNCEMENT_PRIORITIES = ["normal", "important", "urgent"] as const;
export const ANNOUNCEMENT_VISIBILITIES = ["public", "internal"] as const;

/**
 * An update — "Grand Entry delayed to 1:30". Emailing participants is a flag on the announcement
 * rather than a separate send screen, so the update and its email cannot say different things.
 */
export const createAnnouncementSchema = z.object({
  title: shortTextSchema,
  body: longTextSchema,
  /** Present when the update concerns one competition — "Jigging moved to the gym". */
  competitionId: ulidSchema.optional(),
  publishAt: instantSchema.optional(),
  expiresAt: instantSchema.optional(),
  priority: z.enum(ANNOUNCEMENT_PRIORITIES).default("normal"),
  visibility: z.enum(ANNOUNCEMENT_VISIBILITIES).default("public"),
  pinned: z.boolean().default(false),
  emailParticipants: z.boolean().default(false),
  channel: z.enum(["site", "push", "both"]).default("site"),
});

/** Nullable where a value can be removed — `expiresAt: null` makes it evergreen again. */
export const updateAnnouncementSchema = z.object({
  title: shortTextSchema.optional(),
  body: longTextSchema.optional(),
  competitionId: ulidSchema.nullable().optional(),
  publishAt: instantSchema.nullable().optional(),
  expiresAt: instantSchema.nullable().optional(),
  priority: z.enum(ANNOUNCEMENT_PRIORITIES).optional(),
  visibility: z.enum(ANNOUNCEMENT_VISIBILITIES).optional(),
  pinned: z.boolean().optional(),
  emailParticipants: z.boolean().optional(),
  channel: z.enum(["site", "push", "both"]).optional(),
});

export type CreateEventInput = z.infer<typeof createEventSchema>;
export type CreateSessionInput = z.infer<typeof createSessionSchema>;
export type CreateRegistrationWindowInput = z.infer<typeof createRegistrationWindowSchema>;
